/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React from 'react';
import {
  AppBar,
  Breadcrumbs,
  styled,
  Toolbar,
  Typography,
  useMediaQuery,
  Menu,
  MenuItem,
  IconButton,
  Avatar,
} from '@mui/material';
import { Link, useMatch } from 'react-router-dom';
// import { ReactComponent as Logo } from '../images/fema-logo-blue.svg';
import { ReactComponent as TempoLogo } from '../images/tempo_full_color_DKGray.svg';
import { ReactComponent as ImpactLogo } from '../images/IMPACT_DrkBlu_FULLCOLOR_ICON.svg';
import { incidentPageSections } from '../pages/IncidentPage';
import { DrawerButton } from './MapDrawer';
import { incidentConfigByIdAtom } from '../config/api';
import { useAtom, useAtomValue } from 'jotai';
import ArcGISAuth from '../auth/ArcGISAuth';
import { userAtom } from '../auth/userAtoms';
import { FeedbackButton } from '../feedback/FeedbackButton';
import { getCsrfToken } from '../services/arcgisProxy';

const Header = () => {
  const match = useMatch({
    path: ':incidentId/:sectionId/:viewType/:attributeId?',
  });
  const matches = useMediaQuery('(min-width:1000px)');
  const desktop = useMediaQuery('(min-width: 769px)');
  const section = incidentPageSections.find(section => match?.params.sectionId === section.key);
  const currentIncident = useAtomValue(incidentConfigByIdAtom(match?.params.incidentId));
  const [user, setUser] = useAtom(userAtom);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-XSRF-TOKEN': getCsrfToken() },
      });
    } catch (error) {
      console.error('Error during sign out:', error);
    }
    setUser(null);
    handleClose();
  };

  return (
    <AppBar
      sx={{
        zIndex: theme => theme.zIndex.drawer + 1,
        position: 'sticky',
        boxShadow: 'none',
        top: 0,
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#fff',
          color: '#005288',
          padding: '0.2rem 0',
          height: '100%',
        }}
      >
        <StyledLink
          color="inherit"
          to="/"
          sx={{
            height: '50px',
            '@media (max-height: 900px)': {
              height: '50px',
            },
          }}
        >
          <TempoLogo height={'100%'} style={{ paddingRight: 10 }} aria-label="TEMPO" />
        </StyledLink>

        {matches && (
          <Typography
            variant="h6"
            sx={{
              mr: 2,
              fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Tool for Emergency Management Prioritization & Operations
          </Typography>
        )}
        <Spacer />

        <a
          href="https://newlighttechnologies.com/impact"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            textDecoration: 'none',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ImpactLogo
            height="40px"
            style={{
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
            }}
            onMouseOver={e => (e.currentTarget.style.opacity = '0.8')}
            onMouseOut={e => (e.currentTarget.style.opacity = '1')}
          />
        </a>
      </Toolbar>
      <Toolbar variant="dense">
        <DrawerButton />
        <Breadcrumbs
          sx={{ color: 'white', fontSize: desktop ? '17px' : '14px' }}
          aria-label="breadcrumb"
        >
          <StyledLink color="inherit" to="/">
            Dashboard
          </StyledLink>
          {match?.params.incidentId && (
            <StyledLink
              color="inherit"
              to={`/${encodeURIComponent(match.params.incidentId)}/population/map`}
            >
              {currentIncident?.name ?? match.params.incidentId}
            </StyledLink>
          )}
          {match != null && (
            <Typography sx={{ color: 'white', fontSize: desktop ? '17px' : '14px' }}>
              {section?.title}
            </Typography>
          )}
        </Breadcrumbs>
        <Spacer />
        <Link
          color="inherit"
          to="/wowcast"
          style={{
            color: 'white',
            backgroundColor: 'rgb(0, 120, 174)',
            padding: '0.5rem 1rem',
            marginRight: '1rem',
            borderRadius: 2,
            textDecoration: 'none',
          }}
        >
          WOWcast
        </Link>
        <Link
          to="/subscriptions"
          style={{
            color: 'white',
            backgroundColor: 'rgb(0, 120, 174)',
            padding: '0.5rem 1rem',
            marginRight: '1rem',
            borderRadius: 2,
            textDecoration: 'none',
          }}
        >
          TEMPO Alerts
        </Link>
        <FeedbackButton />

        {user ? (
          <>
            <IconButton
              onClick={handleClick}
              size="small"
              aria-controls={open ? 'user-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user.fullName?.[0] || user.username[0]}
              </Avatar>
            </IconButton>
            <Menu
              id="user-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              onClick={handleClose}
            >
              <MenuItem disabled>{user.fullName || user.username}</MenuItem>
              <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
            </Menu>
          </>
        ) : (
          <ArcGISAuth />
        )}
      </Toolbar>
    </AppBar>
  );
};

const StyledLink = styled(Link)`
  color: #fff;
  text-decoration: none;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Spacer = styled('div')`
  flex-grow: 1;
`;

export default Header;
